/* ==========================================================================
   Bulk Confirmation Page (PORTAL-26)

   Shown after a multi-product prescription submission. Lists each product's
   outcome — reference ID on success, error message on failure.
   ========================================================================== */

import { useLocation, Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { PageContainer } from '@/components/layout/PageContainer'
import { usePortalSlug } from '@/context/PortalContext'
import { CheckCircle, XCircle, Printer, FileText, AlertTriangle } from 'lucide-react'
import type { BulkPrescriptionResult } from '@/api/types'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface BulkConfirmationState {
  results: BulkPrescriptionResult[]
  successfulCount: number
  failedCount: number
  sectionMeta: { product_display_name: string }[]
  patientName: string
  submittedAt: string
}

/* --------------------------------------------------------------------------
   Utilities
   -------------------------------------------------------------------------- */

function formatDateTime(isoString: string): string {
  const date = new Date(isoString)
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  return `${formattedDate} at ${formattedTime}`
}

function handlePrint(): void {
  window.print()
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function BulkConfirmation() {
  const location = useLocation()
  const slug = usePortalSlug()
  const state = location.state as BulkConfirmationState | null

  // Defensive fallback for direct navigation without state.
  if (!state) {
    return (
      <PageContainer size="sm" className="py-12">
        <Card className="text-center">
          <AlertTriangle className="h-10 w-10 mx-auto text-[var(--color-warning)] mb-3" />
          <h1 className="text-xl font-semibold text-[var(--color-charcoal)] mb-2">
            Submission details unavailable
          </h1>
          <p className="text-sm text-[var(--color-gray-600)] mb-6">
            We couldn't find the results of your submission. They may already
            have been delivered — check your prescription history.
          </p>
          <Link to={`/rx/${slug}/history`}>
            <Button variant="primary">Go to prescription history</Button>
          </Link>
        </Card>
      </PageContainer>
    )
  }

  const { results, successfulCount, failedCount, sectionMeta, patientName, submittedAt } = state
  const allSucceeded = failedCount === 0
  const allFailed = successfulCount === 0

  const titleText = allSucceeded
    ? results.length > 1
      ? 'Prescriptions Submitted'
      : 'Prescription Submitted'
    : allFailed
      ? 'Submission Failed'
      : 'Partial Submission'

  const subtitleText = allSucceeded
    ? results.length > 1
      ? `${successfulCount} prescriptions sent for processing`
      : 'Your prescription has been sent for processing'
    : allFailed
      ? 'None of the prescriptions could be submitted'
      : `${successfulCount} succeeded, ${failedCount} failed`

  return (
    <PageContainer size="sm" className="py-12">
      <div className="text-center">
        <div className="flex justify-center mb-6">
          <div
            className={`w-20 h-20 rounded-full flex items-center justify-center ${
              allSucceeded
                ? 'bg-[var(--color-success-light)]'
                : allFailed
                  ? 'bg-[var(--color-error-light)]'
                  : 'bg-[var(--color-warning-light)]'
            }`}
          >
            {allSucceeded ? (
              <CheckCircle
                className="h-12 w-12 text-[var(--color-success)]"
                strokeWidth={1.5}
              />
            ) : allFailed ? (
              <XCircle
                className="h-12 w-12 text-[var(--color-error)]"
                strokeWidth={1.5}
              />
            ) : (
              <AlertTriangle
                className="h-12 w-12 text-[var(--color-warning)]"
                strokeWidth={1.5}
              />
            )}
          </div>
        </div>

        <h1 className="font-serif text-2xl sm:text-3xl text-[var(--color-charcoal)] mb-2">
          {titleText}
        </h1>
        <p className="text-[var(--color-gray-600)] mb-8">{subtitleText}</p>

        <Card className="text-left mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-[var(--color-gray-500)]" />
            <h2 className="text-sm font-semibold text-[var(--color-charcoal)] uppercase tracking-wide">
              Summary
            </h2>
          </div>

          <div className="space-y-3">
            {patientName && (
              <div className="flex justify-between py-2 border-b border-[var(--color-gray-200)]">
                <span className="text-sm text-[var(--color-gray-600)]">Patient</span>
                <span className="text-sm font-medium text-[var(--color-charcoal)]">
                  {patientName}
                </span>
              </div>
            )}
            {submittedAt && (
              <div className="flex justify-between py-2 border-b border-[var(--color-gray-200)]">
                <span className="text-sm text-[var(--color-gray-600)]">Submitted</span>
                <span className="text-sm font-medium text-[var(--color-charcoal)]">
                  {formatDateTime(submittedAt)}
                </span>
              </div>
            )}

            {results.map((result) => {
              const succeeded = result.status === 'submitted'
              const productName =
                sectionMeta[result.index]?.product_display_name ??
                `Product ${result.index + 1}`
              return (
                <div
                  key={`${result.index}-${result.catalog_product_id}`}
                  className={`p-3 rounded-lg border ${
                    succeeded
                      ? 'border-[var(--color-success-light)] bg-[var(--color-success-light)]/40'
                      : 'border-[var(--color-error-light)] bg-[var(--color-error-light)]/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[var(--color-charcoal)] truncate">
                        {productName}
                      </div>
                      {succeeded ? (
                        <div className="text-xs text-[var(--color-gray-600)] mt-1">
                          Confirmation:{' '}
                          {result.reference_id ? (
                            <Link
                              to={`/rx/${slug}/confirmation/${result.reference_id}`}
                              state={{
                                patientName,
                                productName,
                                submittedAt,
                              }}
                              className="font-mono text-[var(--pharmacy-primary)] hover:underline"
                            >
                              {result.reference_id}
                            </Link>
                          ) : (
                            <span className="font-mono">{result.reference_id}</span>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-[var(--color-error)] mt-1">
                          {result.error || 'Submission failed.'}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {succeeded ? (
                        <CheckCircle className="h-5 w-5 text-[var(--color-success)]" />
                      ) : (
                        <XCircle className="h-5 w-5 text-[var(--color-error)]" />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </Card>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={`/rx/${slug}/prescribe`}>
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-auto min-w-[200px]"
            >
              Submit Another Prescription
            </Button>
          </Link>
          <Button
            variant="secondary"
            size="lg"
            onClick={handlePrint}
            className="w-full sm:w-auto min-w-[200px]"
          >
            <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
            Print Confirmation
          </Button>
        </div>
      </div>

      <style>{`
        @media print {
          header, nav, button {
            display: none !important;
          }
          body { background: white !important; }
          [class*="PageContainer"] {
            max-width: 100% !important;
            padding: 20px !important;
          }
          /* Reference IDs are <Link>s — keep them in print; the action
             buttons are already hidden by the header, nav, button rule. */
          a { color: inherit !important; text-decoration: none !important; }
        }
      `}</style>
    </PageContainer>
  )
}

export default BulkConfirmation
